import { colors } from '@styles/colors'
import { zIndexes } from '@styles/z-indexes'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ROLE,
  SIZE,
} from 'baseui/modal'
import { useStyletron } from 'styletron-react'
import { BaseButton } from '../base-button'
import { HeadingS, ParagraphM } from '../base-typography'
import { useModalScrollBar } from './hooks/use-modal-scroll-bar'

interface ConfirmModalProps {
  onClose: () => void
  onConfirm: () => void
  isOpen: boolean
  isLoading?: boolean
  title: string
  description?: string
  cancelLabel?: string
  confirmLabel?: string
  type?: 'default' | 'danger' | 'warning'
  fluid?: boolean
}

export const ConfirmModal = ({
  onConfirm,
  onClose,
  isOpen,
  isLoading,
  title,
  description,
  cancelLabel = '취소',
  confirmLabel = '확인',
  type = 'default',
  fluid = true,
}: ConfirmModalProps) => {
  const [css] = useStyletron()
  useModalScrollBar(isOpen)

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      closeable
      animate
      autoFocus={false}
      size={SIZE.default}
      role={ROLE.dialog}
      overrides={{
        Root: {
          style: {
            zIndex: zIndexes.confirmModal,
          },
        },
      }}
    >
      <ModalHeader>
        <HeadingS minHeight="32px">{title}</HeadingS>
      </ModalHeader>
      {description && (
        <ModalBody>
          <ParagraphM
            color={colors.gray500}
            whiteSpace="pre-wrap"
            minHeight="16px"
          >
            {description}
          </ParagraphM>
        </ModalBody>
      )}
      <ModalFooter
        className={css({
          display: 'flex',
          gap: '16px',
          justifyContent: 'flex-end',
          marginBottom: '12px',
        })}
      >
        <BaseButton
          $type={type === 'danger' ? 'tertiary' : 'secondary'}
          fluid={fluid}
          onClick={onClose}
          overrides={
            type === 'danger'
              ? {
                  BaseButton: {
                    style: {
                      minWidth: '120px',
                    },
                  },
                }
              : {}
          }
        >
          {cancelLabel}
        </BaseButton>
        <BaseButton
          fluid={fluid}
          onClick={onConfirm}
          isLoading={isLoading}
          overrides={
            type === 'danger'
              ? {
                  BaseButton: {
                    style: {
                      minWidth: '120px',
                      color: colors.white,
                      backgroundColor: colors.red400,
                      ':hover': {
                        color: colors.white,
                        backgroundColor: colors.red500,
                      },
                    },
                  },
                }
              : {}
          }
        >
          {confirmLabel}
        </BaseButton>
      </ModalFooter>
    </Modal>
  )
}
